import type { SearchSchemaInput } from '@tanstack/react-router';
import { parseEnum, validateListSearch, type ListSearch } from '@/shared/lib/listSearch';
import {
  AgencyClubsControllerListAgencyClubsV1Relationship,
  AgencyClubsControllerListAgencyClubsV1SortBy,
  type AgencyClubsControllerListAgencyClubsV1Params,
} from '@/api/generated/model';

/**
 * URL state for the clubs grid.
 *
 * Same rule as every list screen: the filters live in the URL, not in
 * `useState`, so a filtered view is shareable and survives a refresh. The grid
 * differs from a table only in how it *pages* — see the note on `page` below.
 *
 * The enums come from the generated model rather than being re-typed here, so a
 * relationship or sort field added on the backend arrives as a compile error at
 * the places that switch on it, not as a silently unhandled value.
 */
export type ClubRelationship = AgencyClubsControllerListAgencyClubsV1Relationship;
export type ClubSortField = AgencyClubsControllerListAgencyClubsV1SortBy;

export interface ClubsSearch extends Omit<ListSearch, 'sortBy'> {
  /**
   * Narrowed from the shared `string` to the endpoint's own enum. The list DTO
   * rejects an unknown value with a 400 rather than ignoring it, so a sort
   * field the backend does not have must never get as far as the request.
   */
  sortBy?: ClubSortField | undefined;
  relationship?: ClubRelationship | undefined;
  /**
   * Narrows to one league. Not part of the clubs page's URL state — it is how
   * the transfer-windows screen asks for "my clubs in this league" without a
   * second endpoint. An unknown id returns an empty page; a malformed one 400s,
   * which is why nothing constructs this by hand: it comes from a league the
   * API already returned.
   */
  leagueId?: string | undefined;
}

/** Relationship warmth, ordered strongest first — matches the backend's enum. */
export const CLUB_RELATIONSHIPS = [
  AgencyClubsControllerListAgencyClubsV1Relationship.strong,
  AgencyClubsControllerListAgencyClubsV1Relationship.active,
  AgencyClubsControllerListAgencyClubsV1Relationship.cold,
] as const;

const SORT_FIELDS = [
  AgencyClubsControllerListAgencyClubsV1SortBy.recent,
  AgencyClubsControllerListAgencyClubsV1SortBy.name,
  AgencyClubsControllerListAgencyClubsV1SortBy.relationship,
  AgencyClubsControllerListAgencyClubsV1SortBy.country,
  AgencyClubsControllerListAgencyClubsV1SortBy.budget,
] as const;

/**
 * The sort control offers presets, not a field plus a direction.
 *
 * "Biggest budget" is only ever wanted descending and "Club name" only ever
 * ascending, so exposing two controls would mostly offer combinations nobody
 * picks. The URL still carries the backend's own `sortBy`/`sortOrder` pair — the
 * preset is a UI affordance over them, not a third vocabulary.
 */
export const CLUB_SORT_PRESETS = [
  { key: 'recent', sortBy: SORT_FIELDS[0], sortOrder: 'desc' },
  { key: 'name', sortBy: SORT_FIELDS[1], sortOrder: 'asc' },
  { key: 'relationship', sortBy: SORT_FIELDS[2], sortOrder: 'asc' },
  { key: 'budget', sortBy: SORT_FIELDS[4], sortOrder: 'desc' },
  { key: 'country', sortBy: SORT_FIELDS[3], sortOrder: 'asc' },
] as const satisfies readonly {
  key: string;
  sortBy: ClubSortField;
  sortOrder: 'asc' | 'desc';
}[];

export type ClubSortPresetKey = (typeof CLUB_SORT_PRESETS)[number]['key'];

/** The preset the current URL state corresponds to; the default when it matches none. */
export function clubSortPresetKey(search: ClubsSearch): ClubSortPresetKey {
  const match = CLUB_SORT_PRESETS.find(
    (preset) => preset.sortBy === search.sortBy && preset.sortOrder === search.sortOrder,
  );
  return match?.key ?? 'recent';
}

export function clubSortPreset(key: ClubSortPresetKey): (typeof CLUB_SORT_PRESETS)[number] {
  // `find` cannot miss for a key of this type, but the non-null assertion is
  // avoided anyway — the default is the same one `clubSortPresetKey` falls back
  // to, so the control and the query can never disagree.
  return CLUB_SORT_PRESETS.find((preset) => preset.key === key) ?? CLUB_SORT_PRESETS[0];
}

/**
 * Coerces raw URL params into `ClubsSearch`.
 *
 * `sortBy` is narrowed against the backend's enum on top of what
 * `validateListSearch` does: a hand-edited `?sortBy=budget2` would otherwise
 * reach the API and come back as a 400 the user can do nothing about, because
 * the list DTO rejects unknown values rather than ignoring them.
 *
 * `page` is kept and validated, but the grid never writes to it — pages are
 * appended by the infinite query, not navigated between. It survives so that a
 * hand-edited `?page=3` still starts from a coherent place rather than being
 * silently dropped.
 */
export function validateClubsSearch(
  search: Partial<Record<keyof ClubsSearch, unknown>> & SearchSchemaInput,
): ClubsSearch {
  const base = validateListSearch(search);
  const sortBy = parseEnum(base.sortBy, SORT_FIELDS);

  return {
    ...base,
    sortBy,
    sortOrder: sortBy ? base.sortOrder : undefined,
    relationship: parseEnum(search.relationship, CLUB_RELATIONSHIPS),
  };
}

/**
 * Maps URL state onto the generated query params.
 *
 * The names already match the backend's, so this only drops the empty keys:
 * `forbidNonWhitelisted` validation means sending `search: undefined` is fine
 * but `search: ''` is a filter for the empty string, not the absence of one.
 */
export function toAgencyClubsParams(
  search: ClubsSearch,
): AgencyClubsControllerListAgencyClubsV1Params {
  return {
    limit: search.limit,
    ...(search.sortBy && search.sortOrder
      ? { sortBy: search.sortBy, sortOrder: search.sortOrder }
      : {}),
    ...(search.search ? { search: search.search } : {}),
    ...(search.relationship ? { relationship: search.relationship } : {}),
    ...(search.leagueId ? { leagueId: search.leagueId } : {}),
  };
}
